import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import SignIn from './components/Auth/SignIn';
import VerifyCode from './components/Auth/VerifyCode';
import AccountSetup from './components/Auth/AccountSetup';
import ShippingAddressConfirmation from './components/Auth/ShippingAddressConfirmation';
import ThankYouPreShipment from './components/Auth/ThankYouPreShipment';
import PreparingForShipping from './components/Auth/PreparingForShipping';
import IndividualDashboard from './components/IndividualDashboard/IndividualDashboard';
import HomeDashboard from './components/HomeDashboard/HomeDashboard';
import DeviceSetup from './components/DeviceSetup/DeviceSetup';
import DeviceSettings from './components/HomeDashboard/DeviceSettings';
import { GuestDeviceSetupView } from './components/GuestDeviceSetup/GuestDeviceSetupView';
import HearthWifiView from './components/HearthWifi/HearthWifiView';
import ConfigureHiglos from './components/ConfigureHiglos/ConfigureHiglos';
import { AppShell } from './components/Shell/AppShell';
import { onAuthStateChanged } from "firebase/auth";
import { auth, API_BASE_URL } from "./firebase/firebaseConfig";

// Dev-only preview route — used by the screenshot capture script to render
// screens in isolation with hardcoded props (no real auth required).
const PreviewRoute = process.env.NODE_ENV === 'development'
  ? require('./preview/PreviewRoute').default
  : null;


const LoadingSpinner = () => (
  <div className="flex min-h-screen w-full flex-col items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto mb-3"></div>
      <p className="text-[13px] text-slate-500">Loading…</p>
    </div>
  </div>
);

function App() {
  const [step, setStep] = useState('signin');
  const [email, setEmail] = useState('');
  const [userType, setUserType] = useState('');
  const [familyIDs, setFamilyIDs] = useState([]);
  const [familyName, setFamilyName] = useState('');
  const [numOfFamilies, setNumOfFamilies] = useState(0);
  const [selectedFamilyID, setSelectedFamilyID] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Shipping-related state
  const [shipped, setShipped] = useState(false);
  const [preparingForShipping, setPreparingForShipping] = useState(false);
  const [shippingAddressesConfirmed, setShippingAddressesConfirmed] = useState(false);
  const [nameFromServer, setNameFromServer] = useState(null);
  const [nameAtOrder, setNameAtOrder] = useState(null);
  const [shippingAddressAtOrder, setShippingAddressAtOrder] = useState(null);
  const [shippingCountryAtOrder, setShippingCountryAtOrder] = useState(null);
  // Per-Hearth addresses confirmed via /user/confirmShippingAddresses.
  // Added to the /user/userinfo response so PreparingForShipping /
  // ThankYouPreShipment can render every Hearth's address (not just the
  // order-time one). Default to [] when the field is absent on older
  // backends.
  const [shippingAddresses, setShippingAddresses] = useState([]);
  const [justCompletedShipping, setJustCompletedShipping] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      // /hearth/* pages are device-facing and fully client-side (PLAN P2.1):
      // don't spend a cloud-function call on userinfo for a visitor who is only
      // here to generate a WiFi code — the page needs none of this state.
      if (window.location.pathname.startsWith('/hearth/')) {
        setIsLoading(false);
        return;
      }
      if (currentUser) {
        setIsAuthenticated(true);
        setEmail(currentUser.email);
        try {
          const idToken = await currentUser.getIdToken();
          const response = await fetch(`${API_BASE_URL}/user/userinfo?email=${encodeURIComponent(currentUser.email)}`, {
            headers: {
              'Authorization': `Bearer ${idToken}`
            }
          });
          const data = await response.json();

          if (data.needsSetup) {
            setUserType(data.userType);
            setFamilyIDs(data.familyIDs);
            setFamilyName(data.familyName);
            setNumOfFamilies(data.numOfFamilies);

            // Set shipping-related state
            setShipped(data.shipped || false);
            setPreparingForShipping(data.preparingForShipping || false);
            setShippingAddressesConfirmed(data.shippingAddressesConfirmed || false);
            setNameFromServer(data.name || null);
            setNameAtOrder(data.nameAtOrder || null);
            setShippingAddressAtOrder(data.shippingAddressAtOrder || null);
            setShippingCountryAtOrder(data.shippingCountryAtOrder || null);
            setShippingAddresses(
              Array.isArray(data.shippingAddresses) ? data.shippingAddresses : [],
            );

            // Routing logic based on user type and shipped status
            if (data.userType === 'invitedUser') {
              // Invited users skip shipping entirely - go straight to account setup
              setStep('setup');
            } else if (data.shipped === true || data.shipped === 'true') {
              // Devices already shipped - go to name + family setup
              setStep('setup');
            } else if (data.preparingForShipping === true || data.preparingForShipping === 'true') {
              // Devices are being prepared for shipping - address can no longer be changed
              setStep('preparingForShipping');
            } else {
              // Devices not yet shipped - check if addresses confirmed
              if (!data.shippingAddressesConfirmed) {
                setStep('shippingSetup');
              } else {
                setStep('thankYou');
              }
            }
          } else {
            setStep('dashboard');
          }
        } catch (error) {
          console.error('Error fetching user info:', error);
        }
      } else {
        setIsAuthenticated(false);
        setStep('signin');
      }
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleEmailSubmit = (email) => {
    setEmail(email);
    setStep('verify');
  };

  const handleVerificationSuccess = () => {
    setIsAuthenticated(true);
    setIsLoading(true);
    // Don't set step here - let onAuthStateChanged determine the correct page
  };

  const handleGoBackToSignIn = () => {
    setStep('signin');
  };

  const handleSetupComplete = () => {
    // One-shot session flag so /app/hearths shows the Stage-2 banner only
    // on the fresh arrival from Stage-1. The banner clears itself on the
    // first Hearth tap and never reappears for this session.
    try {
      sessionStorage.setItem('higlo.showSetupStep2Banner', '1');
    } catch (_) { /* private mode or blocked storage — just skip */ }
    setStep('dashboard');
  };

  const handleShippingComplete = (confirmedAddresses) => {
    setJustCompletedShipping(true);
    // Carry the addresses the user just confirmed straight through to the
    // thank-you page. Without this the page falls back to the single
    // order-time address and a split shipment only shows the first Hearth.
    if (Array.isArray(confirmedAddresses) && confirmedAddresses.length > 0) {
      setShippingAddresses(confirmedAddresses);
    }
    setStep('thankYou');
  };

  const handleManageFamily = (familyID) => {
    setSelectedFamilyID(familyID);
    setStep('home');
  };

  const handleSetupDevice = (familyID) => {
    setSelectedFamilyID(familyID);
    setStep('deviceSetup');
  };

  const handleConfigureHiglos = () => {
    setStep('configureHiglos');
  };

  const handleDeviceSettings = (familyID) => {
    setSelectedFamilyID(familyID);
    setStep('deviceSettings');
  };

  const handleReturn = () => {
    setStep('dashboard');
  };

  // Preview routes bypass the auth gate entirely so screenshot capture doesn't
  // wait on Firebase. Only mounted in development builds.
  const isPreview = typeof window !== 'undefined' && window.location.pathname.startsWith('/preview/');
  // /app/* uses its own auth wiring inside AppShell, so it doesn't need
  // App.js's userinfo loading either.
  const isAppShell = typeof window !== 'undefined' && window.location.pathname.startsWith('/app/');
  // /hearth/* pages are device-facing, pre-auth and fully client-side (PLAN
  // P2.1): a customer arriving from the Hearth's QR must not wait on Firebase
  // auth resolution or the userinfo fetch — the page renders immediately.
  const isHearthTool = typeof window !== 'undefined' && window.location.pathname.startsWith('/hearth/');
  if (isLoading && !isPreview && !isAppShell && !isHearthTool) {
    return <LoadingSpinner />;
  }

  // Pre-auth / setup flow lives at "/", post-auth shell lives under "/app/*".
  const preAuthElement =
    step === 'signin' ? <SignIn onEmailSubmit={handleEmailSubmit} /> :
    step === 'verify' ? <VerifyCode email={email} onVerificationSuccess={handleVerificationSuccess} onGoBack={handleGoBackToSignIn} /> :
    step === 'shippingSetup' ? <ShippingAddressConfirmation email={email} numOfFamilies={numOfFamilies} shippingAddressAtOrder={shippingAddressAtOrder} shippingCountryAtOrder={shippingCountryAtOrder} onComplete={handleShippingComplete} /> :
    step === 'preparingForShipping' ? <PreparingForShipping email={email} numOfFamilies={numOfFamilies} shippingAddressAtOrder={shippingAddressAtOrder} shippingAddresses={shippingAddresses} nameFromServer={nameFromServer} /> :
    step === 'thankYou' ? <ThankYouPreShipment isReturningUser={!justCompletedShipping} email={email} numOfFamilies={numOfFamilies} shippingAddressAtOrder={shippingAddressAtOrder} shippingAddresses={shippingAddresses} nameFromServer={nameFromServer} /> :
    step === 'setup' ? <AccountSetup email={email} userType={userType} familyIDs={familyIDs} familyName={familyName} numOfFamilies={numOfFamilies} nameFromServer={nameFromServer} nameAtOrder={nameAtOrder} onSetupComplete={handleSetupComplete} /> :
    step === 'dashboard' ? <Navigate to="/app/hearths" replace /> :
    /* legacy fallbacks — remain reachable while the post-auth tabs are still being built */
    step === 'configureHiglos' ? <ConfigureHiglos onReturn={handleReturn} /> :
    step === 'home' ? <HomeDashboard familyID={selectedFamilyID} onReturn={handleReturn} /> :
    step === 'deviceSettings' ? <DeviceSettings familyID={selectedFamilyID} onReturn={handleReturn} /> :
    step === 'deviceSetup' ? <DeviceSetup familyID={selectedFamilyID} onReturn={handleReturn} /> :
    <Navigate to="/" replace />;

  // Previews render OUTSIDE the BrowserRouter so preview screens can use
  // their own MemoryRouter for useParams without a nested-router error.
  if (isPreview && PreviewRoute) {
    return <PreviewRoute />;
  }

  return (
    <Router>
      <Routes>
        <Route path="/deviceSetupLocal" element={<GuestDeviceSetupView />} />
        {/* PLAN P2.1 — the WiFi sign-in QR generator the Hearth's no-network
            screen links to. Pre-auth, fully client-side; the path is a device
            constant (wifiSigninPath) so a rename must change both. */}
        <Route path="/hearth/wifi" element={<HearthWifiView />} />
        <Route path="/app/*" element={<AppShell />} />
        <Route path="/*" element={preAuthElement} />
      </Routes>
    </Router>
  );
}

export default App;
